import { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'

import {
  FAR_BADGE_PERCENT,
  FAR_FILE_INVOICE,
  FAR_GEAR,
  FAR_GIFT,
  FAR_LIST_ALT,
  FAS_BADGE_PERCENT,
  FAS_FILE_INVOICE,
  FAS_GEAR,
  FAS_GIFT,
  FAS_LIST_ALT
} from 'const/icons'
import routes from 'const/routes'
import i18n from 'strings/i18n'

import { useAccountFeatures, useAccountSummary } from 'const/syfApiQueries'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import isLoanAccount from 'helpers/isLoanAccount'
import { useAtomValue } from 'jotai'
import { statementsFlowRefAtom } from 'store/atoms'
import { SAMS } from 'const/account/clients'
import {
  IconContainer,
  MenuContainer,
  MenuItem,
  MenuList,
  MenuText
} from './subcomponents'

interface MenuOptionsProps {
  activeLink: string
  showAccountServiceMenu: boolean
  accountId: string
}

const MenuOptions = ({
  activeLink,
  showAccountServiceMenu,
  accountId
}: MenuOptionsProps): ReactElement => {
  const { data: accountFeatures } = useAccountFeatures(accountId)
  const { data: accountSummary } = useAccountSummary(accountId)
  const statementsFlowRef = useAtomValue(statementsFlowRefAtom)

  const { accessToRewardsMfe, accessToRewards } =
    accountFeatures?.rewardDetails || {}
  const isSamsClient = accountSummary?.product?.clientId === SAMS

  /*
   * Sam's Club: show Rewards only when isSamsRewardsMfeEnabled flag is ON.
   * Non-Sam's Club: rely on existing conditions.
   */
  const isRewardsButtonEnabled =
    accessToRewardsMfe &&
    accessToRewards &&
    (!isSamsClient || isFeatureFlagEnabled('isSamsRewardsMfeEnabled'))

  const isPromotionsButtonEnabled = accountFeatures?.accessToPromotions

  const isAccountTypeLoan = isLoanAccount(accountSummary?.product?.type)
  const isStatementsButtonEnabled =
    accountFeatures?.accessToStatements && !isAccountTypeLoan

  const isActivityActive =
    activeLink === routes.ACTIVITY || activeLink === routes.DISPUTES
  const isStatementsActive = activeLink === routes.STATEMENTS
  const isPromotionsActive = activeLink === routes.PROMOTIONS
  const isAccountServicesActive = activeLink === routes.ACCOUNT_SERVICES
  const isRewardsActive = activeLink === routes.REWARDS

  return (
    <MenuContainer aria-label="Account navigation">
      <MenuList>
        <MenuItem $isActive={isActivityActive}>
          <NavLink
            data-type="activity"
            data-reason="activity"
            data-object="button"
            data-analytics="clicktrackingevent"
            data-test="activity-nav-link"
            to={routes.ACTIVITY}
          >
            <IconContainer
              icon={isActivityActive ? FAS_LIST_ALT : FAR_LIST_ALT}
            />
            <MenuText weight="bold" isInline>
              {i18n({
                labels: 'activityMenu'
              })}
            </MenuText>
          </NavLink>
        </MenuItem>
        {isStatementsButtonEnabled && (
          <MenuItem $isActive={isStatementsActive}>
            <NavLink
              data-cy="statements-nav-link"
              data-type="statements"
              data-reason="statements"
              data-object="button"
              data-analytics="clicktrackingevent"
              data-test="statements-nav-link"
              to={routes.STATEMENTS}
              onClick={() => {
                statementsFlowRef.current = 'accountfeatures_statements'
              }}
            >
              <IconContainer
                icon={isStatementsActive ? FAS_FILE_INVOICE : FAR_FILE_INVOICE}
              />
              <MenuText weight="bold" isInline>
                {i18n({
                  labels: 'statements'
                })}
              </MenuText>
            </NavLink>
          </MenuItem>
        )}
        {isPromotionsButtonEnabled && (
          <MenuItem $isActive={isPromotionsActive}>
            <NavLink
              data-type="promotions"
              data-reason="left nav"
              data-object="button"
              data-analytics="clicktrackingevent"
              data-test="promotions-nav-link"
              to={routes.PROMOTIONS}
            >
              <IconContainer
                icon={
                  isPromotionsActive ? FAS_BADGE_PERCENT : FAR_BADGE_PERCENT
                }
              />
              <MenuText weight="bold" isInline>
                {i18n({
                  labels: 'promotionsMenu'
                })}
              </MenuText>
            </NavLink>
          </MenuItem>
        )}
        {showAccountServiceMenu && (
          <MenuItem $isActive={isAccountServicesActive}>
            <NavLink
              data-cy="account-services-nav-link"
              data-type="account services"
              data-reason="account services"
              data-object="button"
              data-analytics="clicktrackingevent"
              data-test="account-services-nav-link"
              to={routes.ACCOUNT_SERVICES}
            >
              <IconContainer
                icon={isAccountServicesActive ? FAS_GEAR : FAR_GEAR}
              />
              <MenuText weight="bold" isInline>
                {i18n({
                  labels: 'accountsServices'
                })}
              </MenuText>
            </NavLink>
          </MenuItem>
        )}
        {isRewardsButtonEnabled && (
          <MenuItem $isActive={isRewardsActive}>
            <NavLink
              data-type="rewards"
              data-reason="rewards"
              data-object="button"
              data-analytics="clicktrackingevent"
              data-test="rewards-nav-link"
              to={routes.REWARDS}
            >
              <IconContainer icon={isRewardsActive ? FAS_GIFT : FAR_GIFT} />
              <MenuText weight="bold" isInline>
                {i18n({ button: 'rewards' })}
              </MenuText>
            </NavLink>
          </MenuItem>
        )}
      </MenuList>
    </MenuContainer>
  )
}

export default MenuOptions
